import React from "react";
import { FaBolt, FaRocket, FaPalette } from "react-icons/fa";

const CreativeVibrant = ({ data }) => {
    const {
        to,
        clientAddress,
        clientPhone,
        clientEmail,
        title,
        date,
        dueDate,
        invoiceItems,
        subTotal,
        taxRate,
        taxAmount,
        discount,
        totalAmount,
        notes,
        terms,
        companyDetails
    } = data;

    return (
        <div className="w-full bg-white p-12 font-sans tracking-tight text-slate-900 min-h-[1100px] flex flex-col relative overflow-hidden">
            {/* Background Decorations - Subtle Gradients */}
            <div className="absolute top-0 right-0 w-[50%] h-[30%] bg-gradient-to-br from-slate-100 to-transparent -rotate-12 translate-x-20 -translate-y-20 rounded-[5rem]"></div>
            <div className="absolute bottom-0 left-0 w-[40%] h-[20%] bg-gradient-to-tr from-slate-50 to-transparent rotate-12 -translate-x-20 translate-y-20 rounded-[5rem]"></div>

            <div className="relative z-10 flex-1 flex flex-col justify-between">
                <div>
                    {/* Header */}
                    <div className="flex justify-between items-center mb-16 px-4">
                        <div className="space-y-4">
                            <div className="flex items-center gap-4">
                                <div className="w-14 h-14 bg-slate-900 rounded-xl flex items-center justify-center text-white text-2xl shadow-xl shadow-slate-100">
                                    <FaRocket />
                                </div>
                                <div className="text-3xl font-black uppercase tracking-tighter text-slate-900 italic leading-none">
                                    {companyDetails?.name}
                                </div>
                            </div>
                            <div className="text-[10px] font-bold text-slate-500 uppercase tracking-[0.2em] flex items-center gap-2 pl-1">
                                <div className="w-1.5 h-1.5 bg-slate-400 rounded-full"></div> {companyDetails?.address}
                            </div>
                        </div>

                        <div className="text-right">
                            <div className="text-xs font-black uppercase tracking-[0.4em] text-slate-400 mb-2">Invoice Document</div>
                            <h1 className="text-7xl font-black text-slate-900 tracking-tighter leading-none italic uppercase -mr-2"># {title}</h1>
                        </div>
                    </div>

                    {/* Meta Info */}
                    <div className="grid grid-cols-2 gap-10 mb-16">
                        <div className="bg-slate-900 rounded-2xl p-10 text-white flex justify-between items-end shadow-2xl relative overflow-hidden">
                            <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-full -mr-16 -mt-16"></div>
                            <div className="space-y-2 relative z-10">
                                <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">Billed To</div>
                                <h2 className="text-3xl font-black italic tracking-tighter">{to}</h2>
                                <p className="text-xs font-medium text-slate-400 max-w-[200px] italic">{clientAddress}</p>
                            </div>
                            <div className="text-right relative z-10">
                                <div className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-1">Due Date</div>
                                <div className="text-lg font-black italic tracking-tighter">{dueDate}</div>
                            </div>
                        </div>

                        <div className="p-10 flex flex-col justify-center space-y-4 bg-slate-50 rounded-2xl border border-slate-100">
                            <div className="flex justify-between border-b border-slate-200 pb-4">
                                <span className="text-[10px] font-black uppercase tracking-widest text-slate-400 italic">Issue Date</span>
                                <span className="text-sm font-black text-slate-900 italic tracking-tighter">{date}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-[10px] font-black uppercase tracking-widest text-slate-400 italic">Method</span>
                                <span className="text-sm font-black text-slate-900 italic tracking-tighter uppercase">Bank Transfer</span>
                            </div>
                        </div>
                    </div>

                    {/* Table */}
                    <div className="mb-16">
                        <table className="w-full">
                            <thead>
                                <tr className="text-[10px] font-black uppercase tracking-widest text-slate-400 italic bg-slate-50 border-y border-slate-100">
                                    <th className="py-4 text-left pl-8">Item Description</th>
                                    <th className="py-4 text-center px-4">Qty</th>
                                    <th className="py-4 text-right px-4">Rate</th>
                                    <th className="py-4 text-right pr-8">Subtotal</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 border-b border-slate-100">
                                {(invoiceItems || []).map((item, idx) => (
                                    <tr key={idx} className="group">
                                        <td className="py-8 pl-8 group-hover:bg-slate-50 transition-all">
                                            <div className="text-lg font-black text-slate-900 tracking-tighter italic uppercase">{item.item}</div>
                                            <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-1">Professional Service</div>
                                        </td>
                                        <td className="py-8 text-center text-sm font-black text-slate-400 italic leading-none">{item.qty}</td>
                                        <td className="py-8 text-right text-sm font-black text-slate-900 italic tracking-tighter leading-none pr-4">Nu. {parseFloat(item.unitPrice).toLocaleString()}</td>
                                        <td className="py-8 text-right pr-8 group-hover:bg-slate-50 transition-all text-xl font-black text-slate-900 tracking-tighter italic leading-none">
                                            Nu. {parseFloat(item.amount).toLocaleString()}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                <div className="space-y-12">
                    {/* Bottom Row */}
                    <div className="flex justify-between items-end px-4">
                        <div className="max-w-[400px] space-y-6">
                            <div className="relative pl-6">
                                <div className="absolute left-0 top-0 bottom-0 w-1 bg-slate-900 rounded-full"></div>
                                <p className="text-xs font-medium text-slate-500 leading-relaxed italic pr-10">
                                    {notes || "Thank you for choosing us for your professional needs. We value our partnership and look forward to delivering exceptional quality in every document."}
                                </p>
                            </div>
                            <div className="flex items-center gap-4 text-[10px] font-black uppercase tracking-widest text-slate-400 italic">
                                <FaBolt className="text-slate-400" /> Quality Service & Professional Standards Guaranteed
                            </div>
                        </div>

                        <div className="bg-slate-900 rounded-3xl p-12 text-white shadow-2xl relative group overflow-hidden border border-slate-800">
                            <div className="absolute bottom-0 right-0 w-48 h-48 bg-white/5 rounded-full -mr-24 -mb-24 blur-3xl group-hover:bg-white/10 transition-all duration-700"></div>
                            <div className="space-y-5 relative z-10 w-64">
                                <div className="flex justify-between text-[11px] font-black uppercase tracking-widest opacity-50 italic">
                                    <span>Subtotal</span>
                                    <span>Nu. {parseFloat(subTotal).toLocaleString()}</span>
                                </div>
                                <div className="flex justify-between text-[11px] font-black uppercase tracking-widest opacity-50 italic">
                                    <span>Tax ({taxRate}%)</span>
                                    <span>Nu. {parseFloat(taxAmount).toLocaleString()}</span>
                                </div>
                                {discount > 0 && (
                                    <div className="flex justify-between text-[11px] font-black uppercase tracking-widest text-slate-400 italic">
                                        <span>Discount</span>
                                        <span>- Nu. {parseFloat(discount).toLocaleString()}</span>
                                    </div>
                                )}
                                <div className="pt-8 border-t border-white/10 mt-8 flex justify-between items-end">
                                    <div className="space-y-1">
                                        <div className="text-[10px] font-black uppercase tracking-[0.3em] text-slate-400 italic">Grand Total</div>
                                        <div className="text-5xl font-black tracking-tighter italic">Nu. {parseFloat(totalAmount).toLocaleString()}</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Small Footer */}
                    <div className="flex justify-between items-end text-[10px] font-black uppercase tracking-[0.5em] text-slate-300 px-4 border-t border-slate-50 pt-8 mt-8">
                        <div>Professional Document Series</div>
                        <div className="text-slate-900 border-b border-slate-400 pb-1">NexrBook Professional</div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CreativeVibrant;
